/// <amd-module name="Repos/Editor/Components/CodeDiffEditor" />
import * as React from "react";
import { ChangesContext, IChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { IChangesNavigationService, IDiffNavigation } from "Repos/ChangesCommon/Contracts";
import { IDiffViewerProps } from "Repos/Common/Contracts/Editor";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import * as Resources from "Repos/Editor/Resources";
import { ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { DiffEditor } from "VSS/Features/Monaco/Components/DiffEditor";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getEditorUserPreferencesService } from "../UserPreferencesService";
import { unregisterDefaultKeyBindings } from "./Shortcuts";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssThemeService } from "VSS/Platform/Theming";

export class CodeDiffEditor<TVersionDescriptor> extends VssComponent<IDiffViewerProps> {
    private diffOrientation: DiffViewerOrientation;
    private diffEditor: monaco.editor.IDiffEditor;
    private messageDismissed = new ObservableValue(false);
    private performedInitialNav = false;
    private screenSizeCache: ScreenSize;
    private timerManagement = new TimerManagement();
    private totalAddedLines: number = 0;
    private totalRemovedLines: number = 0;

    constructor(props: IDiffViewerProps, context: IVssComponentContext) {
        super(props, context);

        const preferences = getEditorUserPreferencesService(context.pageContext).getPreferences();
        preferences.subscribe(() => this.diffEditor && this.diffEditor.updateOptions(this.getDiffEditorOptions(this.screenSizeCache)));
    }

    public render() {
        this.diffOrientation = DiffViewerOrientation.SideBySide;
        if (this.props.diffOrientation && ObservableLike.getValue(this.props.diffOrientation) === DiffViewerOrientation.Inline) {
            this.diffOrientation = DiffViewerOrientation.Inline;
        }

        const { addDiffKeyboardShortcuts, showMarginForIcons } = this.props;

        return (
            <ChangesContext.Consumer>
                {(changesContext: IChangesContext<TVersionDescriptor>) => (
                    <ScreenSizeObserver>
                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                            this.screenSizeCache = screenSizeProps.screenSize;

                            let truncatedMessage: JSX.Element | null = null;
                            if (this.props.modifiedContent.contentTruncated || this.props.originalContent.contentTruncated) {
                                truncatedMessage = (
                                    <Observer dismissed={this.messageDismissed}>
                                        {(props: { dismissed: boolean }) => {
                                            return (
                                                !props.dismissed && (
                                                    <MessageBar
                                                        className="flex-noshrink"
                                                        onDismiss={() => (this.messageDismissed.value = true)}
                                                        severity={MessageBarSeverity.Warning}
                                                    >
                                                        {Resources.DiffContentTruncated}
                                                    </MessageBar>
                                                )
                                            );
                                        }}
                                    </Observer>
                                );
                            }

                            const diffEditor = (
                                <DiffEditor
                                    actions={_ => {
                                        let actions = [getEditorUserPreferencesService(this.context.pageContext).getAction(true)];

                                        if (addDiffKeyboardShortcuts) {
                                            if (!actions) {
                                                actions = [];
                                            }

                                            actions.push({
                                                id: "tfs-pr-next-diff",
                                                label: Resources.NextDiff,
                                                keybindings: [monaco.KeyCode.F7],
                                                run: (e: monaco.editor.ICodeEditor) => {
                                                    changesContext.navigationService.nextDiff();
                                                }
                                            });

                                            actions.push({
                                                id: "tfs-pr-previous-diff",
                                                label: Resources.PreviousDiff,
                                                keybindings: [monaco.KeyMod.Shift | monaco.KeyCode.F7],
                                                run: (e: monaco.editor.ICodeEditor) => {
                                                    changesContext.navigationService.previousDiff();
                                                }
                                            });
                                        }

                                        return actions;
                                    }}
                                    className={css("repos-diff-editor flex-grow", showMarginForIcons && "repos-diff-editor-with-margin-icon")}
                                    originalContent={this.props.originalContent.content}
                                    modifiedContent={{
                                        content: this.props.modifiedContent.content,
                                        contentUri: this.props.modifiedContent.resourceUri
                                    }}
                                    fileExtension={this.props.modifiedContent.metadata.extension}
                                    fileUrl={this.props.modifiedContent.resourceUri}
                                    editorOptions={this.getDiffEditorOptions(screenSizeProps.screenSize)}
                                    editorRef={(ref: monaco.editor.IEditor) => {
                                        this.onEditorRef(ref, changesContext.navigationService);
                                    }}
                                    onContentUpdated={this.onContentUpdated}
                                    widgets={this.props.diffWidgets}
                                    delayedHighlighting={this.delayedHighlighting}
                                    getSelectedTextLink={this.props.getCodeSelectionLinkUrl}
                                />
                            );

                            if (truncatedMessage) {
                                return (
                                    <div className="flex-column flex-grow">
                                        {truncatedMessage}
                                        <div className="flex-row flex-grow">{diffEditor}</div>
                                    </div>
                                );
                            } else {
                                return diffEditor;
                            }
                        }}
                    </ScreenSizeObserver>
                )}
            </ChangesContext.Consumer>
        );
    }

    public componentDidMount() {
        super.componentDidMount();

        this.observe([
            {
                propertyName: "diffOrientation",
                observer: value => {
                    if (this.diffOrientation !== value) {
                        this.diffOrientation = value;
                        if (this.diffEditor) {
                            this.diffEditor.updateOptions(this.getDiffEditorOptions(this.screenSizeCache));

                            if (this.props.onDiffOrientationChanged) {
                                this.props.onDiffOrientationChanged(value);
                            }
                        }
                    }
                }
            },
            {
                propertyName: "scrollToLine",
                observer: value => {
                    if (value === undefined) {
                        this.nextDiffFromModifiedLine(0);
                    } else {
                        const newPosition = { column: 0, lineNumber: value };
                        this.diffEditor.getModifiedEditor().setPosition(newPosition);
                        this.diffEditor.revealPositionInCenter(newPosition);
                    }
                }
            }
        ]);
    }

    
    private decorations: string[] = [];

    /**
     * Provide highlighting when a monaco range is being passed into the diff editor
     * @param selectedRanges
     * @param theme 
     */
    private delayedHighlighting = (selectedRanges: monaco.IRange[], theme: string) => {
        const themedClassName = `${theme}-highlight`;

        // Only providing highlighting on the modified editor instance of diff editor
        const modifiedEditor = this.diffEditor.getModifiedEditor();
        const deltaDecorations = modifiedEditor.deltaDecorations.bind(modifiedEditor);

        // Create a map of the monaco ranges with the startline number as the key of the map
        // This helps identifying all provided highlighting ranges on every mouse move
        const locationRangesMap = new Map<number, monaco.Range[]>();
        selectedRanges.forEach(range => {
            const ranges = locationRangesMap.get(range.startLineNumber) || [];
            ranges.push(new monaco.Range(range.startLineNumber, range.startColumn, range.endLineNumber, range.endColumn));
            locationRangesMap.set(range.startLineNumber, ranges);
        });

        let highlightDecorationId: string[] = [];

        // Helper method to highlight with the specified colour on the given range
        const setHighlightRange = (range?: monaco.Range | null) => {
            highlightDecorationId = deltaDecorations(highlightDecorationId, range ? [{
                range,
                options: { className: themedClassName }
            }] : []);
        };

        const handleMouseMove = (e: monaco.editor.IEditorMouseEvent) => {
            const eventRange = e.target.range;
            if (eventRange) {
                const ranges = locationRangesMap.get(eventRange.startLineNumber) ?? [];
                for (const range of ranges) {
                    if (range.intersectRanges(eventRange)) {
                        return setHighlightRange(range);
                    }
                }
            }
            setHighlightRange(null);
        };

        modifiedEditor.onMouseMove(handleMouseMove);
        modifiedEditor.onMouseLeave(() => setHighlightRange(null));
        modifiedEditor.onMouseDown(() => {
            setHighlightRange(null);
            if (isFeatureFlagEnabled(this.context.pageContext, "Git.PullRequest.MonacoLinkToCodeSelection")) {
                this.decorations = this.diffEditor.getModifiedEditor().deltaDecorations(this.decorations, []);
            }
        });
        modifiedEditor.onMouseUp(() => setHighlightRange(null));
    };

    private onEditorRef(ref: monaco.editor.IEditor, navigationService: IChangesNavigationService<TVersionDescriptor>): void {
        this.diffEditor = ref as monaco.editor.IDiffEditor;
        this.props.onLoaded && this.props.onLoaded();

        // Disable every Ctrl-K chord shortcut as they would prevent our standard Ctrl-K to add a link.
        unregisterDefaultKeyBindings(this.diffEditor.getOriginalEditor(), "ctrl+K");
        unregisterDefaultKeyBindings(this.diffEditor.getModifiedEditor(), "ctrl+K")

        if (isFeatureFlagEnabled(this.context.pageContext, "Git.PullRequest.MonacoLinkToCodeSelection")) {
            const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
            const theme = themeService.getCurrentTheme().isDark ? "vs-dark" : "vs";
            const themedClassName = `${theme}-highlight`
            if (this.props.initialSelectedText) {
                if (this.props.initialSelectedText.type === 1) { 
                    this.decorations = this.diffEditor.getOriginalEditor().deltaDecorations([], [
                        {
                            range: this.props.initialSelectedText,
                            options: { className: themedClassName }
                        }
                    ]);
                    this.diffEditor.getOriginalEditor().revealRangeInCenterIfOutsideViewport(this.props.initialSelectedText);
                          
                    this.diffEditor.getOriginalEditor().onMouseDown(() => {
                        this.diffEditor.getOriginalEditor().deltaDecorations(this.decorations, []);
                    });
                }
                else { 
                    this.decorations = this.diffEditor.getModifiedEditor().deltaDecorations([], [
                        {
                            range: this.props.initialSelectedText,
                            options: { className: themedClassName },
                            
                        }
                    ]);
                    this.diffEditor.getModifiedEditor().revealRangeInCenterIfOutsideViewport(this.props.initialSelectedText);         
            
                    this.diffEditor.getModifiedEditor().onMouseDown(() => {
                        this.diffEditor.getModifiedEditor().deltaDecorations(this.decorations, []);
                    });
                }
            }
        }
            
        this.diffEditor.onDidUpdateDiff(() => {
            this.onDiffUpdated(navigationService);
        });

        this.diffEditor.getModifiedEditor().onDidChangeCursorPosition(() => {
            this.updateDiffNavigationFromModifiedPosition(navigationService);
        });
        this.diffEditor.getOriginalEditor().onDidChangeCursorPosition(() => {
            this.updateDiffNavigationFromOriginalPosition(navigationService);
        });
    }

    private onContentUpdated = (editor: monaco.editor.IEditor) => {
        if (this.props.onContentChanged) {
            const model = editor.getModel() as monaco.editor.IDiffEditorModel;
            this.props.onContentChanged(model.modified.getValue());
        }
    };

    private getDiffEditorOptions(screenSize: ScreenSize): monaco.editor.IDiffEditorOptions {
        const hideLineNumberGutter = screenSize === ScreenSize.xsmall;

        const hasWidgets = !!this.props.diffWidgets;
        const diffEditorOptions = getEditorUserPreferencesService(this.context.pageContext).getEditorOptions(
            !!this.props.editable,
            undefined,
            !hideLineNumberGutter && hasWidgets,
            hideLineNumberGutter
        ) as monaco.editor.IDiffEditorOptions;
        diffEditorOptions.renderSideBySide = this.diffOrientation === DiffViewerOrientation.SideBySide ? true : false;
        return diffEditorOptions;
    }

    private updateDiffNavigationFromModifiedPosition(navigationService: IChangesNavigationService<TVersionDescriptor>): void {
        if (!navigationService) {
            return
        }

        const diffNavigation: IDiffNavigation = {
            hasNextDiff: false,
            hasPreviousDiff: false,
            nextDiff: this.nextDiff,
            previousDiff: this.prevDiff,
            totalAddedLines: this.totalAddedLines,
            totalRemovedLines: this.totalRemovedLines
        };

        const position = this.diffEditor.getModifiedEditor().getPosition() || { lineNumber: 0 };
        const changes = this.diffEditor.getLineChanges() || [];
        for (const change of changes) {
            if (change.modifiedStartLineNumber > position.lineNumber) {
                diffNavigation.hasNextDiff = true;
            }
            if (
                change.modifiedStartLineNumber > 0 && // when the first line of a file is deleted, do not say that there is a previous diff available
                change.modifiedStartLineNumber < position.lineNumber
            ) {
                diffNavigation.hasPreviousDiff = true;
            }
        }

        navigationService.setDiffNavigation(diffNavigation);
    }

    private updateDiffNavigationFromOriginalPosition(navigationService: IChangesNavigationService<TVersionDescriptor>): void {
        if (!navigationService) {
            return
        }

        const diffNavigation: IDiffNavigation = {
            hasNextDiff: false,
            hasPreviousDiff: false,
            nextDiff: this.nextDiff,
            previousDiff: this.prevDiff,
            totalAddedLines: this.totalAddedLines,
            totalRemovedLines: this.totalRemovedLines
        };

        const position = this.diffEditor.getOriginalEditor().getPosition() || { lineNumber: 0 };
        const changes = this.diffEditor.getLineChanges() || [];
        for (const change of changes) {
            if (change.originalStartLineNumber > position.lineNumber) {
                diffNavigation.hasNextDiff = true;
            }
            if (
                change.originalStartLineNumber > 0 && // when the first line of a file is deleted, do not say that there is a previous diff available
                change.originalStartLineNumber < position.lineNumber
            ) {
                diffNavigation.hasPreviousDiff = true;
            }
        }

        navigationService.setDiffNavigation(diffNavigation);
    }

    private onDiffUpdated(navigationService: IChangesNavigationService<TVersionDescriptor>): void {
        this.totalAddedLines = 0;
        this.totalRemovedLines = 0;

        const lineChanges = this.diffEditor.getLineChanges() || [];
        for (const lineChange of lineChanges) {
            if (lineChange.originalEndLineNumber > 0) {
                this.totalRemovedLines += lineChange.originalEndLineNumber - lineChange.originalStartLineNumber + 1;
            }
            if (lineChange.modifiedEndLineNumber > 0) {
                this.totalAddedLines += lineChange.modifiedEndLineNumber - lineChange.modifiedStartLineNumber + 1;
            }
        }

        this.updateDiffNavigationFromModifiedPosition(navigationService);

        if (!this.performedInitialNav) {
            this.performedInitialNav = true;

            if (this.props.scrollToLine && this.props.scrollToLine.value) {
                const newPosition = { column: 0, lineNumber: this.props.scrollToLine.value };

                this.diffEditor.getModifiedEditor().setPosition(newPosition);
                this.diffEditor.revealPositionInCenter(newPosition);
            } else if (navigationService) {
                if (navigationService.getNavigateToFirstDiffOnLoad()) {
                    this.nextDiffFromModifiedLine(0);
                } else if (navigationService.getNavigateToLastDiffOnLoad()) {
                    // Need to give the diff time to load to avoid scroll being offset once additions are loaded
                    // 300ms chosen based on the PR Discussion timer & experiments with different times around 300ms
                    this.timerManagement.setTimeout(() => {
                        this.prevDiffFromModifiedLine(Number.MAX_SAFE_INTEGER)
                    }, 300);
                }
            }
        }
    }

    // returns true if able to navigate to the next diff
    private nextDiffFromModifiedLine(lineNumber: number): boolean {
        const changes = this.diffEditor.getLineChanges() || [];
        for (const change of changes) {
            if (change.modifiedStartLineNumber > lineNumber) {
                const newPosition = { column: 0, lineNumber: change.modifiedStartLineNumber };
                this.diffEditor.getModifiedEditor().setPosition(newPosition);
                this.diffEditor.revealPositionInCenter(newPosition);
                return true;
            }
        }

        return false;
    }

    // returns true if able to navigate to the next diff
    private nextDiffFromOriginalLine(lineNumber: number): boolean {
        const changes = this.diffEditor.getLineChanges() || [];
        for (const change of changes) {
            if (change.originalStartLineNumber > lineNumber) {
                const newPosition = { column: 0, lineNumber: change.originalStartLineNumber };
                this.diffEditor.getOriginalEditor().setPosition(newPosition);
                this.diffEditor.revealPositionInCenter(newPosition);
                return true;
            }
        }

        return false;
    }

    private nextDiff = () => {
        if (this.diffEditor.getOriginalEditor().hasTextFocus()) {
            const position = this.diffEditor.getOriginalEditor().getPosition();
            this.nextDiffFromOriginalLine(position ? position.lineNumber : 0);
        } else {
            const position = this.diffEditor.getModifiedEditor().getPosition();
            this.nextDiffFromModifiedLine(position ? position.lineNumber : 0);
        }
    };

    // returns true if able to navigate to the previous diff
    private prevDiffFromModifiedLine(lineNumber: number): boolean {
        const changes = this.diffEditor.getLineChanges() || [];
        for (let i = changes.length - 1; i >= 0; i--) {
            if (changes[i].modifiedStartLineNumber < lineNumber) {
                const newPosition = { column: 0, lineNumber: changes[i].modifiedStartLineNumber };
                this.diffEditor.getModifiedEditor().setPosition(newPosition);
                this.diffEditor.revealPositionInCenter(newPosition);
                return true;
            }
        }

        return false;
    }

    // returns true if able to navigate to the previous diff
    private prevDiffFromOriginalLine(lineNumber: number): boolean {
        const changes = this.diffEditor.getLineChanges() || [];
        for (let i = changes.length - 1; i >= 0; i--) {
            if (changes[i].originalStartLineNumber < lineNumber) {
                const newPosition = { column: 0, lineNumber: changes[i].originalStartLineNumber };
                this.diffEditor.getOriginalEditor().setPosition(newPosition);
                this.diffEditor.revealPositionInCenter(newPosition);
                return true;
            }
        }

        return false;
    }

    private prevDiff = () => {
        if (this.diffEditor.getOriginalEditor().hasTextFocus()) {
            const position = this.diffEditor.getOriginalEditor().getPosition() || { lineNumber: 0 };
            this.prevDiffFromOriginalLine(position.lineNumber);
        } else {
            const position = this.diffEditor.getModifiedEditor().getPosition() || { lineNumber: 0 };
            this.prevDiffFromModifiedLine(position.lineNumber);
        }
    };
}
